import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  RefreshCw,
  Bed,
  Users,
  AlertCircle,
  CheckCircle,
  Clock,
  Wrench,
  Calendar,
  Phone,
  Mail,
  Edit3,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import CheckInModal from "./CheckInModal";

const statusConfig = {
  Available: {
    color: "#10B981",
    bgColor: "#D1FAE5",
    icon: CheckCircle,
    label: "Available",
  },
  Occupied: {
    color: "#EF4444",
    bgColor: "#FEE2E2",
    icon: Users,
    label: "Occupied",
  },
  Dirty: {
    color: "#F59E0B",
    bgColor: "#FEF3C7",
    icon: AlertCircle,
    label: "Dirty",
  },
  Reserved: {
    color: "#3B82F6",
    bgColor: "#DBEAFE",
    icon: Clock,
    label: "Reserved",
  },
  Maintenance: {
    color: "#8B5CF6",
    bgColor: "#EDE9FE",
    icon: Wrench,
    label: "Maintenance",
  },
};

const RoomCard = ({ room, onRoomClick }) => {
  const config = statusConfig[room.status] || statusConfig["Available"];
  const Icon = config.icon;

  return (
    <Card
      className={`p-4 cursor-pointer transition-all hover:shadow-md border-2 ${
        room.status === "Available" ? "hover:border-green-300" : ""
      }`}
      style={{
        borderColor: config.color + "40",
        backgroundColor: config.bgColor,
      }}
      onClick={() => onRoomClick(room)}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-2xl font-bold font-heading text-[#2D2C2A]">
            {room.room_number}
          </div>
          <div className="text-xs text-[#7A7873] mt-1">
            {room.category_name}
          </div>
        </div>
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: config.color, color: "white" }}
        >
          <Icon size={20} />
        </div>
      </div>

      <Badge
        className="text-xs font-medium"
        style={{
          backgroundColor: config.color,
          color: "white",
        }}
      >
        {config.label}
      </Badge>

      {room.status === "Occupied" && room.guest_name && (
        <div className="mt-3 pt-3 border-t border-gray-300">
          <div className="text-xs text-[#7A7873]">Guest</div>
          <div className="text-sm font-semibold text-[#2D2C2A] truncate">
            {room.guest_name}
          </div>
          {room.guest_phone && (
            <div className="text-xs text-[#7A7873] mt-1">
              {room.guest_phone}
            </div>
          )}
        </div>
      )}

      {room.status === "Reserved" && room.guest_name && (
        <div className="mt-3 pt-3 border-t border-gray-300">
          <div className="text-xs text-[#7A7873]">Reserved for</div>
          <div className="text-sm font-semibold text-[#2D2C2A] truncate">
            {room.guest_name}
          </div>
        </div>
      )}
    </Card>
  );
};

const FloorSection = ({ floorName, rooms, onRoomClick }) => {
  const stats = rooms.reduce((acc, room) => {
    acc[room.status] = (acc[room.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-bold text-xl text-[#2D2C2A] flex items-center gap-2">
          <Bed size={20} className="text-[#D4AF37]" />
          {floorName}
        </h2>
        <div className="flex gap-2">
          {Object.entries(stats).map(([status, count]) => {
            const config = statusConfig[status];
            return (
              <Badge
                key={status}
                variant="outline"
                className="text-xs"
                style={{
                  borderColor: config.color,
                  color: config.color,
                }}
              >
                {status}: {count}
              </Badge>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {rooms.map((room) => (
          <RoomCard key={room.id} room={room} onRoomClick={onRoomClick} />
        ))}
      </div>
    </div>
  );
};

export default function HotelRoomGrid() {
  const navigate = useNavigate();
  const [roomsData, setRoomsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newStatus, setNewStatus] = useState("");
  const [bookingDetails, setBookingDetails] = useState(null);

  // Reservation form state
  const [reservationData, setReservationData] = useState({
    room_id: "",
    guest_name: "",
    guest_phone: "",
    check_in_date: "",
    check_out_date: "",
    adults: 1,
  });

  const fetchRooms = async () => {
    setLoading(true);
    try {
      const response = await api.get("/hotel/rooms/grid");
      setRoomsData(response.data.rooms || {});
    } catch (error) {
      console.error("Failed to fetch rooms:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get("/hotel/categories");
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  useEffect(() => {
    fetchRooms();
    fetchCategories();
  }, []);

  const handleRoomClick = async (room) => {
    setSelectedRoom(room);

    if (room.status === "Available") {
      setShowCheckInModal(true);
    } else if (room.status === "Occupied" || room.status === "Reserved") {
      // Fetch and show booking details
      if (room.booking_id) {
        try {
          const response = await api.get(`/hotel/bookings/${room.booking_id}`);
          setBookingDetails(response.data.booking);
          setShowBookingModal(true);
        } catch (error) {
          toast.error("Failed to fetch booking details");
        }
      }
    } else if (room.status === "Dirty" || room.status === "Maintenance") {
      // Show status change modal
      setNewStatus("");
      setShowStatusModal(true);
    }
  };

  const handleStatusChange = async () => {
    if (!newStatus) {
      toast.error("Please select a status");
      return;
    }

    try {
      await api.put(`/hotel/rooms/${selectedRoom.id}/status`, {
        status: newStatus
      });
      toast.success(`Room ${selectedRoom.room_number} status updated to ${newStatus}`);
      setShowStatusModal(false);
      setSelectedRoom(null);
      fetchRooms();
    } catch (error) {
      toast.error("Failed to update room status");
    }
  };

  const handleMakeReservation = async () => {
    // Validate
    if (!reservationData.room_id || !reservationData.guest_name || !reservationData.guest_phone || 
        !reservationData.check_in_date || !reservationData.check_out_date) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      // Create guest first
      const guestRes = await api.post("/hotel/guests", {
        name: reservationData.guest_name,
        phone: reservationData.guest_phone,
      });

      // Create reservation
      await api.post("/hotel/bookings/reserve", {
        room_id: reservationData.room_id,
        guest_id: guestRes.data.guest.id,
        check_in_date: reservationData.check_in_date,
        check_out_date: reservationData.check_out_date,
        adults: reservationData.adults,
        status: "Reserved",
      });

      toast.success("Reservation created successfully!");
      setShowReservationModal(false);
      setReservationData({
        room_id: "",
        guest_name: "",
        guest_phone: "",
        check_in_date: "",
        check_out_date: "",
        adults: 1,
      });
      fetchRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create reservation");
    }
  };

  const handleCheckInSuccess = () => {
    setShowCheckInModal(false);
    setSelectedRoom(null);
    fetchRooms();
  };

  const totalRooms = Object.values(roomsData).flat().length;
  const statusCounts = Object.values(roomsData)
    .flat()
    .reduce((acc, room) => {
      acc[room.status] = (acc[room.status] || 0) + 1;
      return acc;
    }, {});

  const occupancyRate =
    totalRooms > 0
      ? (((statusCounts["Occupied"] || 0) / totalRooms) * 100).toFixed(1)
      : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8 max-w-full">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight text-[#2D2C2A]">
            Room Status
          </h1>
          <p className="text-sm text-[#7A7873] mt-1">
            Live room availability and occupancy
          </p>
        </div>
        <Button onClick={fetchRooms} variant="outline" className="gap-2">
          <RefreshCw size={16} />
          Refresh
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <Card className="p-4 border-[#EBE8E2] shadow-sm bg-white">
          <div className="text-xs text-[#7A7873] uppercase tracking-wider font-medium">
            Total Rooms
          </div>
          <div className="text-2xl font-bold text-[#2D2C2A] mt-1">
            {totalRooms}
          </div>
        </Card>

        <Card className="p-4 border-[#EBE8E2] shadow-sm bg-white">
          <div className="text-xs text-[#7A7873] uppercase tracking-wider font-medium">
            Occupancy Rate
          </div>
          <div className="text-2xl font-bold text-[#2D2C2A] mt-1">
            {occupancyRate}%
          </div>
        </Card>

        {Object.entries(statusConfig).map(([status, config]) => {
          const count = statusCounts[status] || 0;
          if (count === 0 && status === "Maintenance") return null;

          return (
            <Card
              key={status}
              className="p-4 border-[#EBE8E2] shadow-sm bg-white"
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: config.color }}
                />
                <div className="text-xs text-[#7A7873] uppercase tracking-wider font-medium">
                  {config.label}
                </div>
              </div>
              <div className="text-2xl font-bold text-[#2D2C2A] mt-1">
                {count}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Room Grid by Floor */}
      <div>
        {Object.keys(roomsData).length === 0 ? (
          <Card className="p-8 text-center">
            <AlertCircle className="mx-auto text-[#7A7873] mb-2" size={48} />
            <p className="text-[#7A7873]">No rooms available</p>
          </Card>
        ) : (
          Object.entries(roomsData)
            .sort(([a], [b]) => {
              // Custom sort: Ground, 1st, 2nd, 3rd, etc.
              const order = [
                "Ground",
                "1st Floor",
                "2nd Floor",
                "3rd Floor",
                "4th Floor",
              ];
              return order.indexOf(a) - order.indexOf(b);
            })
            .map(([floorName, rooms]) => (
              <FloorSection
                key={floorName}
                floorName={floorName}
                rooms={rooms}
                onRoomClick={handleRoomClick}
              />
            ))
        )}
      </div>

      {/* Check-In Modal */}
      {showCheckInModal && selectedRoom && (
        <CheckInModal
          room={selectedRoom}
          categories={categories}
          onClose={() => {
            setShowCheckInModal(false);
            setSelectedRoom(null);
          }}
          onSuccess={handleCheckInSuccess}
        />
      )}

      {/* Status Change Modal */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Change Status - Room {selectedRoom?.room_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Current Status</Label>
              <div className="p-3 bg-gray-100 rounded mt-1">
                <Badge style={{
                  backgroundColor: statusConfig[selectedRoom?.status]?.color,
                  color: "white",
                }}>
                  {selectedRoom?.status}
                </Badge>
              </div>
            </div>

            <div>
              <Label htmlFor="new_status">New Status *</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger id="new_status">
                  <SelectValue placeholder="Select new status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Available">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      Available
                    </div>
                  </SelectItem>
                  <SelectItem value="Dirty">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      Dirty
                    </div>
                  </SelectItem>
                  <SelectItem value="Maintenance">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-purple-500" />
                      Maintenance
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <div className="text-xs text-[#7A7873] mt-1">
                Note: Occupied and Reserved rooms must be checked out first
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowStatusModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleStatusChange}>
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking Details Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Booking Details - Room {selectedRoom?.room_number}
            </DialogTitle>
          </DialogHeader>
          {bookingDetails && (
            <div className="space-y-4">
              {/* Guest Information */}
              <Card className="p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Users size={18} className="text-[#D4AF37]" />
                  Guest Information
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[#7A7873]">Name</div>
                    <div className="font-semibold">{bookingDetails.guest_name}</div>
                  </div>
                  <div>
                    <div className="text-[#7A7873]">Phone</div>
                    <div className="font-semibold flex items-center gap-1">
                      <Phone size={14} />
                      {bookingDetails.guest_phone}
                    </div>
                  </div>
                  <div>
                    <div className="text-[#7A7873]">Email</div>
                    <div className="font-semibold flex items-center gap-1">
                      <Mail size={14} />
                      {bookingDetails.guest_email || "N/A"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[#7A7873]">Booking Code</div>
                    <div className="font-semibold">{bookingDetails.booking_code}</div>
                  </div>
                </div>
              </Card>

              {/* Stay Details */}
              <Card className="p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Calendar size={18} className="text-[#D4AF37]" />
                  Stay Details
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[#7A7873]">Check-In</div>
                    <div className="font-semibold">{bookingDetails.check_in_date}</div>
                  </div>
                  <div>
                    <div className="text-[#7A7873]">Check-Out</div>
                    <div className="font-semibold">{bookingDetails.check_out_date}</div>
                  </div>
                  <div>
                    <div className="text-[#7A7873]">Total Nights</div>
                    <div className="font-semibold">{bookingDetails.total_nights}</div>
                  </div>
                  <div>
                    <div className="text-[#7A7873]">Plan Type</div>
                    <div className="font-semibold">{bookingDetails.plan_type}</div>
                  </div>
                  <div>
                    <div className="text-[#7A7873]">Adults</div>
                    <div className="font-semibold">{bookingDetails.adults}</div>
                  </div>
                  <div>
                    <div className="text-[#7A7873]">Children</div>
                    <div className="font-semibold">{bookingDetails.children || 0}</div>
                  </div>
                </div>
              </Card>

              {/* Billing Summary */}
              <Card className="p-4">
                <h3 className="font-semibold mb-3">Billing Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#7A7873]">Room Rent/Night</span>
                    <span className="font-semibold">₹{parseFloat(bookingDetails.room_rent_per_night).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#7A7873]">Advance Paid</span>
                    <span className="font-semibold text-green-600">₹{parseFloat(bookingDetails.advance_paid || 0).toFixed(2)}</span>
                  </div>
                  {bookingDetails.special_requests && (
                    <div className="pt-2 border-t">
                      <div className="text-[#7A7873]">Special Requests</div>
                      <div className="text-sm">{bookingDetails.special_requests}</div>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowBookingModal(false)}
            >
              Close
            </Button>
            {bookingDetails?.status === "Checked-In" && (
              <Button
                onClick={() => {
                  navigate(`/hotel-checkout/${bookingDetails.id}`);
                }}
                className="bg-[#D4AF37] hover:bg-[#D4AF37]/90"
              >
                Check-Out
              </Button>
            )}
            {bookingDetails?.status === "Reserved" && (
              <Button
                onClick={() => {
                  setShowBookingModal(false);
                  setShowCheckInModal(true);

      {/* Make Reservation Modal */}
      <Dialog open={showReservationModal} onOpenChange={setShowReservationModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Make Reservation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="res_room">Select Room *</Label>
              <Select 
                value={reservationData.room_id} 
                onValueChange={(val) => setReservationData({...reservationData, room_id: val})}
              >
                <SelectTrigger id="res_room">
                  <SelectValue placeholder="Choose available room" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(roomsData)
                    .flat()
                    .filter(r => r.status === "Available")
                    .map(room => (
                      <SelectItem key={room.id} value={room.id.toString()}>
                        {room.room_number} - {room.category_name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="res_name">Guest Name *</Label>
              <Input
                id="res_name"
                value={reservationData.guest_name}
                onChange={(e) => setReservationData({...reservationData, guest_name: e.target.value})}
                placeholder="Full name"
              />
            </div>

            <div>
              <Label htmlFor="res_phone">Guest Phone *</Label>
              <Input
                id="res_phone"
                value={reservationData.guest_phone}
                onChange={(e) => setReservationData({...reservationData, guest_phone: e.target.value})}
                placeholder="Phone number"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="res_checkin">Check-In Date *</Label>
                <Input
                  id="res_checkin"
                  type="date"
                  value={reservationData.check_in_date}
                  onChange={(e) => setReservationData({...reservationData, check_in_date: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="res_checkout">Check-Out Date *</Label>
                <Input
                  id="res_checkout"
                  type="date"
                  value={reservationData.check_out_date}
                  onChange={(e) => setReservationData({...reservationData, check_out_date: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="res_adults">Adults</Label>
              <Input
                id="res_adults"
                type="number"
                min="1"
                value={reservationData.adults}
                onChange={(e) => setReservationData({...reservationData, adults: parseInt(e.target.value)})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowReservationModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleMakeReservation} className="bg-blue-600 hover:bg-blue-700">
              Create Reservation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
                }}
                className="bg-green-600 hover:bg-green-700"
              >
                Check-In Now
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
